import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { SignatureComponent } from '@syncfusion/ej2-angular-inputs';
import { ActivatedRoute } from '@angular/router';
import { DigitalWaiverWidgetService } from './digital-waiver-widget.service';
import { DigitalWaiver } from '../../simulator-booking/models/digital-waiver';
import { SharedModule } from '../../shared/shared.module';
import { SyncfusionModule } from 'src/app/simulator-booking/syncfusion.module';
import { DigitalWaiverEntryList } from '../../simulator-booking/models/digital-waiver-entry-list';

@Component({
  selector: 'digital-waiver-widget',
  templateUrl: './digital-waiver-widget.component.html',
  styleUrls: ['./digital-waiver-widget.component.scss'],
  standalone: true,
  imports: [
    SharedModule,
    SyncfusionModule
  ]
})
export class DigitalWaiverWidgetComponent implements OnInit {
  @ViewChild('signatureObj') signatureObj: SignatureComponent;

  public form = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    emailAddress: new FormControl('', [Validators.required, Validators.email]),
    signature: new FormControl('', [Validators.required]),
    signedDate: new FormControl(null)
  });

  public strokeColor: string = '#000000';
  public backgroundColor: string = '#ffffff';
  public hasSignature: boolean = false;
  public isLoading: boolean = false;
  public hasError: boolean = false;
  public hasSigned: boolean = false;
  public hasSubmissionError: boolean = false;
  public isSubmitting: boolean = false;
  public activeDigitalWaiver: DigitalWaiver;
  private digitalWaiverEntry: DigitalWaiverEntryList;
  private apiKey: string;
  private id: string;
  private userDetails: any;

  constructor(
    private digitalWaiverWidgetService: DigitalWaiverWidgetService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.apiKey = params['apiKey'];
      this.id = params['id'];
      this.getUserOrganization();
    });
  }

  onSignatureChange(): void {
    this.form.get('signature')?.setValue(this.signatureObj.getSignature());
    this.hasSignature = !this.signatureObj.isEmpty();
  }

  getUserOrganization(): void {
    this.isLoading = true;
    this.digitalWaiverWidgetService.getUserOrganization(this.apiKey).subscribe({
      next: (res: any) => {
        this.userDetails = res;
        this.digitalWaiverWidgetService.getActiveDigitalWaiver(res.organization.id, this.apiKey).subscribe({
          next: (waiverRes: any) => {
            this.activeDigitalWaiver = waiverRes;
            this.getDigitalWaiverEntry();
          },
          error: (error) => {
            console.error('Error fetching active waiver:', error);
            this.hasError = true;
            this.isLoading = false;
          }
        });
      },
      error: (error) => {
        console.error('Error fetching organization:', error);
        this.hasError = true;
        this.isLoading = false;
      },
    });
  }

  getDigitalWaiverEntry() {
    this.digitalWaiverWidgetService.getDigitalWaiverEntry(this.apiKey, this.id).subscribe({
      next: (res: any) => {
        this.digitalWaiverEntry = res;
        this.form.patchValue(res);
        if(res.signedDate) {
          this.hasSigned = true;
        }
      },
      error: (error) => {
        console.error('Error fetching booking:', error);
        this.hasError = true;
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
        this.hasError = false;
      }
    });
  }

  clearSignature(): void {
    this.signatureObj?.clear();
    this.hasSignature = false;
  }

  submit(): void {
    if (this.form.valid && this.hasSignature) {
      this.isSubmitting = true;

      const now = new Date();
      const nowUtc = new Date(Date.UTC(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours(),
        now.getMinutes()
      ));

      const signatureData = this.signatureObj?.getSignature();
      const formData = {
        ...this.form.value,
        id: this.digitalWaiverEntry.id,
        digitalWaiverId: this.activeDigitalWaiver.id,
        bookingId: this.digitalWaiverEntry.bookingId,
        organizationId: this.userDetails.organization.id,
        signature: signatureData,
        sentDate: this.digitalWaiverEntry.sentDate,
        signedDate: nowUtc,
        createdBy: this.userDetails.id,
        lastEditedBy: this.userDetails.id
      };

      this.digitalWaiverWidgetService.updateDigitalWaiverEntry(this.apiKey, formData).subscribe({
        next: () => {
          this.hasSigned = true;
          this.isSubmitting = false;
        },
        error: (error) => {
          console.error('Error submitting waiver:', error);
          this.hasSubmissionError = true;
          this.hasSigned = false;
          this.isSubmitting = false;
        }
      });
    }
  }
}
